import React, { useEffect, useState } from 'react';
import ChevronDown from 'sushiweb/Icons/all/ChevronDown';
import ChevronUp from 'sushiweb/Icons/all/ChevronUp';
import './TransactionsDetails.scss';
import { EmptyState } from '../../commons';
import { InvalidSearchImg } from '../../../images/images';
import { getFullDate } from '../../commons/CommonMethods';
import { Table, CopyText, Tooltip, Loader, Button } from '../../../UiKit';
import { orderModel } from '../../../api';
import { sendEventToJumbo } from '../../../helpers/analytics';
import { useClient } from '../../../api/api-client';
import { useMutation } from 'react-query';
import toast from 'react-hot-toast';

const transactionsTableHeadings = [
  { name: 'Total Paid amount', key: 1 },
  { name: 'Status', key: 2 },
  { name: 'Txn. ref. ID', key: 3 },
  { name: 'Payment mode(s)', key: 4 },
  { name: 'Gateway Txn ID', key: 5 },
  { name: 'Initiated via', key: 6 },
];

const failedTransactionsTableHeadings = [
  { name: 'Failed Transaction', key: 1 },
  { name: 'Status', key: 2 },
  { name: 'Txn. ref. ID', key: 3 },
  { name: 'Payment mode(s)', key: 4 },
  { name: 'Gateway Txn ID', key: 5 },
  { name: 'Initiated via', key: 6 },
];

const refundTableHeadings = [
  { name: 'Refunded amount', key: 1 },
  { name: 'Status', key: 2 },
  { name: 'Refunded to', key: 3 },
  { name: 'Reason', key: 4 },
  { name: 'Refunded by', key: 5 },
  { name: 'RRN (Refund Ref. Number)', key: 6 },
  { name: '', key: 8 },
];

const refundedItemsTableHeadings = [
  { name: 'Refunded item', key: 1 },
  { name: 'Item ID', key: 2 },
  { name: 'Quantity', key: 3 },
  { name: 'Refunded by', key: 4 },
];

const refundDetailsHeadings = [
  { name: 'Stage', key: 1 },
  { name: 'Date/Time Started', key: 2 },
  { name: 'Details', key: 3 },
  { name: 'Status', key: 4 },
  { name: 'Customer communication', key: 5 },
];

const odpTransactionHeadings = [
  { name: 'Promo amount', key: 1 },
  { name: 'Status', key: 2 },
  { name: 'Granted By', key: 3 },
  { name: 'Code', key: 4 },
  { name: 'Valid Till', key: 5 },
];

const delightTransactionHeadings = [
  { name: 'Order Id', key: 1 },
  { name: 'Amount', key: 2 },
  { name: 'Direction', key: 3 },
  { name: 'Created At', key: 4 },
];

export const TransactionsDetails = ({ cartId = null, orderId = null }) => {
  const [transactionsDetails, setTransactionsDetails] = useState(null);
  const [refundDetailsId, setRefundDetailsId] = useState(null);
  const [error, setError] = useState(null);
  const [checkRefundRetry, setcheckRefundRetry] = useState(false);

  useEffect(() => {
    const getRefundDetailsId = (refunds) => {
      if (!refunds) return {};
      let map = {};
      refunds.forEach((item) => {
        map[item.txn_ref_id] = false;
      });
      return map;
    };

    const getTransactionsDetails = () => {
      sendEventToJumbo('get_transaction_details');
      let queryStrings = {};
      queryStrings['cart_id'] = cartId;
      queryStrings['order_id'] = orderId;
      orderModel
        .getTransactionsDetails(queryStrings)
        .then((response) => {
          if (!response || response?.isError) return setError(response);
          sendEventToJumbo('get_transaction_details_successfully');
          setTransactionsDetails({ ...response });
          setRefundDetailsId(
            getRefundDetailsId(response?.refund_transactions?.source),
          );
        })
        .catch((err) => {
          console.log('Transactions Details error', err);
          setError(err);
          sendEventToJumbo('get_transaction_details_failed');
        });
    };

    getTransactionsDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cartId, orderId, checkRefundRetry]);

  if (!transactionsDetails && !error) {
    return <Loader />;
  }

  if (error) {
    return (
      <EmptyState
        img={InvalidSearchImg}
        heading={error?.message || 'There are no refunds against this order'}
        isPop={true}
      />
    );
  }

  return (
    <div className='transactionsDetails modalContent' data-dd-privacy='mask'>
      <TransactionsDetailsComp
        transactionsDetails={transactionsDetails}
        refundDetailsId={refundDetailsId}
        setcheckRefundRetry={setcheckRefundRetry}
      />
    </div>
  );
};

function useFailedRefundRetry(setcheckRefundRetry, mutationConfig = {}) {
  const client = useClient();

  return useMutation({
    mutationKey: ['refund_retry'],
    mutationFn: ({ slug, data }) => {
      client(`${slug}`, { data });
    },
    meta: { hideAlert: true },
    onSettled: () => {
      setcheckRefundRetry((prevState) => !prevState);
      setTimeout(() => {
        setcheckRefundRetry((prevState) => !prevState);
      }, 5000);
    },
    onSuccess: () => {
      toast.success(
        'Refund Initiated, wait few seconds till we are processing the transaction',
      );
    },
    ...mutationConfig,
  });
}

const TransactionsDetailsComp = ({
  transactionsDetails = null,
  refundDetailsId = null,
  setcheckRefundRetry,
}) => {
  const paidTransactions =
    transactionsDetails && transactionsDetails.paid_transactions;
  const refundTransactions =
    transactionsDetails && transactionsDetails.refund_transactions;
  const failedTransactions =
    transactionsDetails && transactionsDetails.failed_transactions;
  const odpTransaction = transactionsDetails.odp_transactions ?? [];
  const delightTransaction = transactionsDetails.delight_transactions ?? [];

  const [tableRefundData, updateRrn] = useState(refundTransactions);
  const [refundDetails, setRefundDetails] = useState(null);

  const { mutate: retryRefund } = useFailedRefundRetry(setcheckRefundRetry);

  useEffect(() => {
    refundDetailsId && setRefundDetails(refundDetailsId);
    updateRrn(refundTransactions);
  }, [refundDetailsId, refundTransactions]);

  const handleDetailsTable = (idx) => {
    let map = {};
    let detailsId = Object.keys(refundDetails);
    detailsId.forEach((item) => {
      map[item] = false;
      if (item === idx) map[item] = !refundDetails[idx];
    });
    setRefundDetails(map);
  };

  const getTransactionsTableData = (transactionsDetails) => {
    if (!transactionsDetails) return;

    const getTransactionsRows = () => {
      const rowsData = [];
      Object.keys(transactionsDetails).map((item) => {
        const transactionDate = getFullDate(
          transactionsDetails[item].created_at * 1000,
        );
        const transactionsObj = {
          1: {
            title: transactionsDetails[item].display_amount || '-',
            subtitle: transactionDate || '-',
          },
          2: {
            title:
              (
                <p
                  style={{
                    color: transactionsDetails[item].transaction_status_color,
                  }}
                >
                  {transactionsDetails[item].transaction_status}
                </p>
              ) || '-',
          },
          3: {
            title: transactionsDetails[item].txn_id || '-',
          },
          4: {
            title: transactionsDetails[item]?.payment_method_type ? (
              <div className='tw-flex tw-w-full'>
                <div className='tw-w-1/5 tw-mr-2 tw-flex tw-justify-center tw-items-center tw-rounded-lg'>
                  {transactionsDetails[item]?.payment_method_logo ? (
                    <img
                      src={transactionsDetails[item]?.payment_method_logo}
                      alt=''
                      width='100%'
                      height='100%'
                    />
                  ) : (
                    ''
                  )}
                </div>
                <div className='tw-flex-col tw-w-full tw-justify-center tw-items-center'>
                  <div className='tw-font-medium'>
                    {transactionsDetails[item]?.payment_method_type}
                  </div>
                  <div className='tw-text-200'>
                    {transactionsDetails[item]?.bank_name}
                  </div>
                </div>
              </div>
            ) : (
              transactionsDetails[item].mode || '-'
            ),
            subtitle: transactionsDetails[item]?.card_number ? (
              <>
                <span className='tw-font-medium small'>Card Number : </span>
                <span className='small'>
                  ****{transactionsDetails[item]?.card_number}
                </span>
              </>
            ) : (
              ''
            ),
          },
          5: {
            title: transactionsDetails[item].gateway_txn_id || '-',
          },
          6: {
            title: transactionsDetails[item].payment_initiator || '-',
          },
        };
        rowsData.push(transactionsObj);
        return true;
      });
      return rowsData;
    };

    const tableData = {
      headings: transactionsTableHeadings,
      rows: getTransactionsRows(),
    };
    return tableData;
  };

  const getFailedTxnTableData = (failedTransactionsDetails) => {
    if (!failedTransactionsDetails) return;

    const getFailedTransactionsRows = () => {
      const rowsData = [];
      Object.keys(failedTransactionsDetails).map((item) => {
        const failedTransactionDate = getFullDate(
          failedTransactionsDetails[item].created_at * 1000,
        );
        const failedTransactionObj = {
          1: {
            title: failedTransactionsDetails[item].display_amount || '-',
            subtitle: failedTransactionDate || '-',
          },
          2: {
            title:
              (
                <p
                  style={{
                    color:
                      failedTransactionsDetails[item].transaction_status_color,
                  }}
                >
                  {failedTransactionsDetails[item].transaction_status}
                </p>
              ) || '-',
          },
          3: {
            title: failedTransactionsDetails[item].txn_id || '-',
          },
          4: {
            title:
              failedTransactionsDetails[item].payment_method_type ||
              failedTransactionsDetails[item].mode ||
              '-',
          },
          5: {
            title: failedTransactionsDetails[item].gateway_txn_id || '-',
          },
          6: {
            title: failedTransactionsDetails[item].payment_initiator || '-',
          },
        };
        rowsData.push(failedTransactionObj);
        return true;
      });
      return rowsData;
    };

    const tableData = {
      headings: failedTransactionsTableHeadings,
      rows: getFailedTransactionsRows(),
    };
    return tableData;
  };

  const getRefundsTable = (refundTransactions, updateRrn) => {
    const ifNoRefundsAvailable = () => {
      return Object.keys(refundTransactions).every(function (value) {
        return !refundTransactions[value].length;
      });
    };

    const retryFailedPayment = (refundId, ticketId, endpoint) => {
      endpoint = endpoint.substring(1, endpoint.length);
      const mutateParams = {
        slug: endpoint,
        data: {
          refund_id: refundId,
          ticket_id: ticketId,
        },
      };
      retryRefund(mutateParams);
    };

    const getRefundRows = () => {
      const rowsData = [];
      Object.keys(refundTransactions).map((refunds) => {
        refundTransactions[refunds] &&
          refundTransactions[refunds].map((refund, index) => {
            let idx = refund.txn_ref_id;
            const refundDate = getFullDate(refund.created_at * 1000);
            const expectedCompletionTime = refund.expected_completion_time;
            const refundObj = {
              1: {
                title: refund.display_amount || '-',
                subtitle:
                  (
                    <>
                      <p className='tw-text-200'>
                        {refundDate && (
                          <>
                            <p className='tw-text-200 tw-font-semibold tw-mr-1'>
                              Initiation Time:{' '}
                            </p>
                            {refundDate}
                          </>
                        )}
                      </p>
                      <p className='tw-text-200'>
                        {expectedCompletionTime && (
                          <>
                            <p className='tw-text-200 tw-font-semibold tw-mr-1'>
                              ETA:{' '}
                            </p>
                            {expectedCompletionTime}
                          </>
                        )}
                      </p>
                    </>
                  ) || '-',
                nestedTable:
                  refund.timeline && refundDetails
                    ? {
                        colSpan: 6,
                        data: (
                          <Table
                            tableData={getRefundDetailsTableData(
                              refund.timeline,
                            )}
                          />
                        ),
                        show: refundDetails?.[idx],
                        estimatedCompletion: refund.estimated_completion
                          ? refund.estimated_completion
                          : null,
                      }
                    : null,
              },
              2: {
                title:
                  (
                    <span className={refund.transaction_status_color}>
                      {refund.transaction_status}
                    </span>
                  ) || '-',
              },
              3: {
                title: refund.payment_method_type || refund.gateway_name || '-',
              },
              4: {
                title: (
                  <span
                    css={`
                      white-space: pre-line;
                    `}
                  >
                    {refund.reason_string || '-'}
                  </span>
                ),
              },
              5: {
                title: (
                  <span
                    css={`
                      white-space: pre-line;
                    `}
                  >
                    {refund.granter || '-'}
                  </span>
                ),
              },
              6: {
                title: refund.rrn_number || '-',
              },
              7: {
                title:
                  refund.timeline && refundDetails ? (
                    <React.Fragment>
                      <span
                        onClick={() => handleDetailsTable(idx)}
                        className='details'
                      >
                        Details
                      </span>
                      {refundDetails[idx] ? (
                        <ChevronUp color='#50b547' />
                      ) : (
                        <ChevronDown color='#50b547' />
                      )}
                    </React.Fragment>
                  ) : null,
              },
              8: {
                title: (
                  <>
                    <div
                      css={`
                        width: 100%;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                      `}
                    >
                      <Button
                        outline
                        title='Retry'
                        size='small'
                        isDisabled={
                          !(refund.transaction_status === 'FAILED') ||
                          !refund.retry_url
                        }
                        onClick={() => {
                          retryFailedPayment(
                            refund.refund_id,
                            refund.ticket_id,
                            refund?.retry_url,
                          );
                        }}
                      />
                    </div>
                  </>
                ),
              },
            };
            rowsData.push(refundObj);
            return true;
          });
        return true;
      });
      return rowsData;
    };

    const getRefundDetailsTableData = (timeline) => {
      let tableData = {};
      const getDetailsRow = () => {
        const rowsData = [];
        timeline.map((item, index) => {
          const date =
            item.start_date !== '--' ? getFullDate(item.start_date) : '--';
          const detailsObj = {
            1: {
              title: index + 1,
            },
            2: {
              title: date,
            },
            3: {
              title: item.details || '-',
            },
            4: {
              title:
                <p className={item.status.color}>{item.status.value}</p> || '-',
            },
            5: {
              title:
                (
                  <Tooltip
                    position='left'
                    border={false}
                    title={
                      <CopyText
                        title={item.customer_communication}
                        customClass='communicationOverflow'
                      />
                    }
                    label={item.customer_communication}
                    fontWeight={300}
                    fontSize={14}
                    widthMaxContent={false}
                    labelWidth={30}
                  />
                ) || '-',
            },
          };
          rowsData.push(detailsObj);
          return true;
        });
        return rowsData;
      };

      tableData['headings'] = refundDetailsHeadings;
      tableData['rows'] = getDetailsRow();
      return tableData;
    };

    const getRefundTableData = () => {
      let tableData = {};
      tableData['headings'] = refundTableHeadings;
      tableData['rows'] = getRefundRows();
      return tableData;
    };

    const noRefunds = ifNoRefundsAvailable();

    if (noRefunds) {
      return (
        <div className='greyStrip'>
          <p className='lighter'>There are no refunds against this order</p>
        </div>
      );
    } else {
      return (
        <div className='refundedDetails'>
          <Table tableData={getRefundTableData()} />
        </div>
      );
    }
  };

  return (
    <React.Fragment>
      {paidTransactions && (
        <div
          className='paidDetails'
          css={`
            & td {
              vertical-align: top;
            }
          `}
        >
          <Table tableData={getTransactionsTableData(paidTransactions)} />
        </div>
      )}
      {getRefundsTable(tableRefundData, updateRrn)}

      {odpTransaction?.length ? (
        <div
          css={`
            margin-top: 1rem;
            flex-direction: column;
          `}
        >
          <h3
            css={`
              margin-bottom: 0.5rem;
            `}
          >
            Promo Codes Created
          </h3>
          <Table tableData={getOdpTransaction(odpTransaction)} />
        </div>
      ) : null}

      {delightTransaction?.length ? (
        <div
          css={`
            margin-top: 1rem;
            flex-direction: column;
          `}
        >
          <h3
            css={`
              margin-bottom: 0.5rem;
            `}
          >
            Delight Transactions
          </h3>
          <Table tableData={getDelightTransaction(delightTransaction)} />
        </div>
      ) : null}

      {failedTransactions && failedTransactions.length > 0 && (
        <div
          css={`
            margin-top: 2rem;
          `}
        >
          <Table
            tableData={getFailedTxnTableData(failedTransactions)}
            tableHeadBackgroundColor='#f7ebec'
          />
        </div>
      )}
    </React.Fragment>
  );
};

const getOdpTransaction = (odpTransaction) => {
  return {
    headings: odpTransactionHeadings,
    rows: Object.keys(odpTransaction).map((key, index) => {
      return {
        1: {
          title: `₹ ${odpTransaction[key].amount}`,
          subtitle: getFullDate(odpTransaction[key].created_at * 1000),
        },
        2: { title: odpTransaction[key].status },
        3: { title: odpTransaction[key].granted_by },
        4: {
          title: odpTransaction[key].code ? (
            <div
              style={{
                background: 'lightgrey',
                color: 'black',
                padding: '2px 4px',
                borderRadius: '5px',
              }}
            >
              {odpTransaction[key].code}
            </div>
          ) : (
            '-'
          ),
        },
        5: {
          title: odpTransaction[key].valid_till
            ? odpTransaction[key].valid_till
            : '-',
        },
      };
    }),
  };
};

const getDelightTransaction = (delightTransaction) => {
  return {
    headings: delightTransactionHeadings,
    rows: Object.keys(delightTransaction).map((key) => {
      return {
        1: {
          title: delightTransaction[key].order_id || '-',
        },
        2: {
          title: `₹ ${delightTransaction[key].amount}` || '-',
        },
        3: {
          title: delightTransaction[key].direction || '-',
        },
        4: {
          title: getFullDate(delightTransaction[key].created_at * 1000) || '-',
        },
      };
    }),
  };
};
